<!-- eslint-disable vue/multi-word-component-names -->
<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'

import { usePagination } from '@/composables/usePagination'
import { useSearch } from '@/composables/useSearch'
import { getFeedbackList, getFeedbackDetail, processFeedback } from '@/api/feedback'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'

// 数据
const loading = ref(false)
const feedbackList = ref([])

// 分页
const { pagination, goToPage, setPageSize, updatePagination, getPaginationParams } = usePagination()

// 搜索
const { searchForm, handleSearch: triggerSearch, resetSearch, getSearchParams } = useSearch({
  initialForm: {
    type: '',
    status: ''
  },
  onSearch: fetchData
})

// 反馈类型映射
const typeMap = {
  1: { label: '功能建议', color: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  2: { label: '系统问题', color: 'bg-red-50 text-red-700 ring-red-600/20' },
  3: { label: '内容错误', color: 'bg-amber-50 text-amber-700 ring-amber-600/20' },
  4: { label: '其他', color: 'bg-slate-50 text-slate-700 ring-slate-600/20' }
}

// status map
const statusMap = {
  0: { label: '待处理', color: 'bg-amber-50 text-amber-700 ring-amber-600/20' },
  1: { label: '已处理', color: 'bg-green-50 text-green-700 ring-green-600/20' }
}

// 类型筛选选项
const typeOptions = [
  { label: '全部类型', value: '' },
  { label: '功能建议', value: 1 },
  { label: '系统问题', value: 2 },
  { label: '内容错误', value: 3 },
  { label: '其他', value: 4 }
]

// 状态筛选选项
const statusOptions = [
  { label: '全部状态', value: '' },
  { label: '待处理', value: 0 },
  { label: '已处理', value: 1 }
]

// 详情窗口
const showDetailModal = ref(false)
const detailLoading = ref(false)
const currentFeedback = ref(null)

// 处理确认窗口
const showProcessModal = ref(false)
const processTarget = ref(null)
const processLoading = ref(false)

// 图片预览
const showImagePreview = ref(false)
const previewImageUrl = ref('')

// 获取数据
async function fetchData() {
  loading.value = true
  try {
    const params = {
      ...getSearchParams(),
      ...getPaginationParams()
    }
    const data = await getFeedbackList(params)
    feedbackList.value = data.records || []
    updatePagination(data)
  } catch (error) {
    ElMessage.error('加载反馈列表失败')
    console.error('加载反馈列表失败:', error)
  } finally {
    loading.value = false
  }
}

// 筛选变化
const handleFilterChange = () => {
  pagination.current = 1
  fetchData()
}

// 换页
const changePage = (page) => {
  goToPage(page)
  fetchData()
}

// 切换每页条数
const changePageSize = (size) => {
  setPageSize(size)
  fetchData()
}

// 查看详情
const handleViewDetail = async (item) => {
  showDetailModal.value = true
  detailLoading.value = true
  try {
    const detail = await getFeedbackDetail(item.id)
    currentFeedback.value = detail
  } catch (error) {
    ElMessage.error('加载反馈详情失败')
    console.error('加载反馈详情失败:', error)
    currentFeedback.value = item
  } finally {
    detailLoading.value = false
  }
}

// 关闭详情窗口
const closeDetailModal = () => {
  showDetailModal.value = false
  currentFeedback.value = null
}

// 打开处理确认窗口
const handleProcess = (item) => {
  processTarget.value = item
  showProcessModal.value = true
}

// 确认处理
const confirmProcess = async () => {
  if (!processTarget.value) return
  processLoading.value = true
  try {
    const targetId = processTarget.value.id
    await processFeedback(targetId)
    ElMessage.success('已标记为已处理')
    showProcessModal.value = false
    // 如果详情窗口打开，同步更新状态
    if (currentFeedback.value?.id === targetId) {
      currentFeedback.value.status = 1
    }
    processTarget.value = null
    fetchData()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
    console.error('处理反馈失败:', error)
  } finally {
    processLoading.value = false
  }
}

// 从详情窗口触发处理
const handleProcessFromDetail = () => {
  if (currentFeedback.value) {
    handleProcess(currentFeedback.value)
  }
}

// 解析图片列表
const parseImages = (images) => {
  if (!images) return []
  return images.split(',').map(url => url.trim()).filter(Boolean)
}

// 预览图片
const openImagePreview = (url) => {
  previewImageUrl.value = url
  showImagePreview.value = true
}

// 关闭图片预览
const closeImagePreview = () => {
  showImagePreview.value = false
  previewImageUrl.value = ''
}

// 格式化时间
const formatDateTime = (time) => {
  if (!time) return '-'
  return new Date(time).toLocaleString('zh-CN')
}

// 截断文本
const truncateText = (text, maxLength = 60) => {
  if (!text) return '-'
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

onMounted(() => {
  fetchData()
})
</script>

<template>
  <ListPageShell>
    <template #header>
    <!-- 页面标题 -->
    <div class="mb-6">
      <h1 class="text-xl font-semibold text-slate-900">意见反馈管理</h1>
      <p class="text-sm text-slate-500 mt-1">查看和处理用户提交的意见反馈</p>
    </div>

    <!-- 筛选栏 -->
    </template>

    <template #filters>
    <PageFilterBar
      body-class="filter-bar-body is-dense"
      content-class="filter-bar-content is-spacious"
      show-refresh
      :refresh-loading="loading"
      @refresh="fetchData"
    >
      <div class="flex items-center gap-4 flex-wrap">
        <!-- 类型筛选 -->
        <select
          v-model="searchForm.type"
          class="form-select-native form-control-sm"
          @change="handleFilterChange"
        >
          <option v-for="opt in typeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>

        <!-- 状态筛选 -->
        <select
          v-model="searchForm.status"
          class="form-select-native form-control-sm"
          @change="handleFilterChange"
        >
          <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>

        <!-- 搜索 & 重置 -->
      </div>

      <template #searchActions>
        <button
          @click="triggerSearch"
          class="btn-primary"
        >
          搜索
        </button>
        <button
          @click="resetSearch"
          class="btn-secondary"
        >
          重置
        </button>
      </template>
    </PageFilterBar>
    </template>

    <!-- 结果信息 -->
    <div class="flex items-center justify-between mb-4">
      <span class="text-sm text-slate-500">共 {{ pagination.total }} 条反馈</span>
    </div>

    <!-- 数据表格 -->
    <div class="list-page-table-panel">
      <div class="data-table-shell">
        <div class="data-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th text-left w-16">ID</th>
                <th class="data-table-th text-left w-24">类型</th>
                <th class="data-table-th text-left">反馈内容</th>
                <th class="data-table-th text-left w-20">图片</th>
                <th class="data-table-th text-left w-24">状态</th>
                <th class="data-table-th text-left w-44">提交时间</th>
                <th class="data-table-th text-center w-28">操作</th>
              </tr>
            </thead>
            <tbody>
              <!-- 加载骨架屏 -->
              <template v-if="loading">
                <tr v-for="n in 5" :key="n" class="animate-pulse">
                  <td class="data-table-td is-center"><div class="h-4 w-10 bg-slate-200 rounded"></div></td>
                  <td class="data-table-td is-center"><div class="h-5 w-16 bg-slate-200 rounded-full"></div></td>
                  <td class="data-table-td"><div class="h-4 w-3/4 bg-slate-200 rounded"></div></td>
                  <td class="data-table-td is-center"><div class="h-4 w-8 bg-slate-200 rounded"></div></td>
                  <td class="data-table-td is-center"><div class="h-5 w-14 bg-slate-200 rounded-full"></div></td>
                  <td class="data-table-td"><div class="h-4 w-32 bg-slate-200 rounded"></div></td>
                  <td class="data-table-td is-center"><div class="flex gap-1 justify-center"><div class="h-7 w-7 bg-slate-200 rounded"></div><div class="h-7 w-7 bg-slate-200 rounded"></div></div></td>
                </tr>
              </template>

              <!-- 空状态 -->
              <tr v-else-if="feedbackList.length === 0">
                <td colspan="7" class="data-table-empty">
                  <div class="flex flex-col items-center">
                    <div class="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                      <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                      </svg>
                    </div>
                    <p class="text-slate-500 text-sm mb-1">暂无反馈数据</p>
                    <p class="text-slate-400 text-xs">用户提交反馈后将会在此展示</p>
                  </div>
                </td>
              </tr>

              <!-- 数据行 -->
              <tr
                v-for="item in feedbackList"
                v-else
                :key="item.id"
                class="data-table-row"
              >
                <!-- ID -->
                <td class="data-table-td text-slate-500">{{ item.id }}</td>

                <!-- 类型 -->
                <td class="data-table-td">
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset"
                    :class="typeMap[item.type]?.color || 'bg-slate-50 text-slate-600 ring-slate-500/20'"
                  >
                    {{ typeMap[item.type]?.label || '未知' }}
                  </span>
                </td>

                <!-- 反馈内容 -->
                <td class="data-table-td">
                  <span class="cursor-pointer hover:text-primary-600 transition-colors" @click="handleViewDetail(item)">
                    {{ truncateText(item.content) }}
                  </span>
                </td>

                <!-- 图片 -->
                <td class="data-table-td">
                  <div v-if="parseImages(item.images).length > 0" class="flex items-center gap-1">
                    <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    <span class="text-xs text-slate-500">{{ parseImages(item.images).length }}张</span>
                  </div>
                  <span v-else class="text-xs text-slate-400">-</span>
                </td>

                <!-- 状态 -->
                <td class="data-table-td">
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset"
                    :class="statusMap[item.status]?.color || 'bg-slate-50 text-slate-600 ring-slate-500/20'"
                  >
                    {{ statusMap[item.status]?.label || '未知' }}
                  </span>
                </td>

                <!-- 提交时间 -->
                <td class="data-table-td text-slate-500">{{ formatDateTime(item.createTime) }}</td>

                <!-- 操作 -->
                <td class="data-table-td is-center">
                  <div class="flex items-center justify-center gap-1">
                    <!-- 查看详情 -->
                    <button
                      @click="handleViewDetail(item)"
                      class="table-action-button is-primary"
                      title="查看详情"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>

                    <!-- 标记已处理 -->
                    <button
                      v-if="item.status === 0"
                      v-permission="'app:feedback:edit'"
                      @click="handleProcess(item)"
                      class="table-action-button is-success"
                      title="标记已处理"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 分页 -->
      <AppPagination
        v-if="pagination.total > 0"
        class="pagination-bridge"
        :pagination="pagination"
        :page-sizes="[10, 20, 50]"
        @page-change="changePage"
        @size-change="changePageSize"
      />
    </div>

    <!-- 详情弹窗 -->
    <div v-if="showDetailModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="closeDetailModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-xl shadow-2xl overflow-hidden max-w-2xl w-full">
          <!-- 头部 -->
          <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 class="text-lg font-semibold text-slate-900">反馈详情</h3>
            <button @click="closeDetailModal" class="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- 内容 -->
          <div class="p-6">
            <!-- 加载状态 -->
            <div v-if="detailLoading" class="animate-pulse space-y-4">
              <div class="flex gap-3">
                <div class="h-5 w-16 bg-slate-200 rounded-full"></div>
                <div class="h-5 w-14 bg-slate-200 rounded-full"></div>
              </div>
              <div class="h-4 w-1/3 bg-slate-200 rounded"></div>
              <div class="space-y-2">
                <div class="h-4 w-full bg-slate-200 rounded"></div>
                <div class="h-4 w-3/4 bg-slate-200 rounded"></div>
                <div class="h-4 w-1/2 bg-slate-200 rounded"></div>
              </div>
            </div>

            <!-- 详情内容 -->
            <div v-else-if="currentFeedback" class="space-y-5">
              <!-- 类型和状态标签 -->
              <div class="flex items-center gap-2 flex-wrap">
                <span
                  class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset"
                  :class="typeMap[currentFeedback.type]?.color"
                >
                  {{ typeMap[currentFeedback.type]?.label || '未知类型' }}
                </span>
                <span
                  class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset"
                  :class="statusMap[currentFeedback.status]?.color"
                >
                  {{ statusMap[currentFeedback.status]?.label || '未知状态' }}
                </span>
              </div>

              <!-- 基本信息 -->
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span class="text-slate-500">反馈ID</span>
                  <p class="text-slate-900 mt-0.5">{{ currentFeedback.id }}</p>
                </div>
                <div>
                  <span class="text-slate-500">用户ID</span>
                  <p class="text-slate-900 mt-0.5">{{ currentFeedback.userId }}</p>
                </div>
                <div class="col-span-2">
                  <span class="text-slate-500">提交时间</span>
                  <p class="text-slate-900 mt-0.5">{{ formatDateTime(currentFeedback.createTime) }}</p>
                </div>
              </div>

              <!-- 分隔线 -->
              <div class="border-t border-slate-100"></div>

              <!-- 反馈内容 -->
              <div>
                <h4 class="text-sm font-medium text-slate-700 mb-2">反馈内容</h4>
                <div class="bg-slate-50 rounded-lg p-4 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{{ currentFeedback.content || '无内容' }}</div>
              </div>

              <!-- 图片预览 -->
              <div v-if="parseImages(currentFeedback.images).length > 0">
                <h4 class="text-sm font-medium text-slate-700 mb-2">图片预览</h4>
                <div class="grid grid-cols-3 gap-3">
                  <div
                    v-for="(img, idx) in parseImages(currentFeedback.images)"
                    :key="idx"
                    class="aspect-square rounded-lg overflow-hidden border border-slate-200 cursor-pointer hover:ring-2 hover:ring-primary-500 transition-all group"
                    @click="openImagePreview(img)"
                  >
                    <img
                      :src="img"
                      :alt="'反馈图片' + (idx + 1)"
                      class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 底部操作 -->
          <div class="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
            <button
              @click="closeDetailModal"
              class="btn-secondary"
            >
              关闭
            </button>
            <button
              v-if="currentFeedback?.status === 0"
              v-permission="'app:feedback:edit'"
              @click="handleProcessFromDetail"
              class="btn-primary"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              标记已处理
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 处理确认弹窗 -->
    <div v-if="showProcessModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="showProcessModal = false"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="bg-white rounded-xl shadow-2xl p-6 max-w-sm mx-4">
          <div class="flex items-center gap-4 mb-4">
            <div class="w-12 h-12 rounded-full flex items-center justify-center bg-green-100 flex-shrink-0">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-slate-900">确认处理</h3>
              <p class="text-sm text-slate-500">确定要将此反馈标记为已处理吗？</p>
            </div>
          </div>
          <div class="flex items-center justify-end gap-3">
            <button
              @click="showProcessModal = false"
              :disabled="processLoading"
              class="btn-secondary"
            >
              取消
            </button>
            <button
              @click="confirmProcess"
              :disabled="processLoading"
              class="btn-primary"
            >
              <svg v-if="processLoading" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              确认
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片预览 -->
    <div v-if="showImagePreview" class="fixed inset-0 z-[60] flex items-center justify-center" @click="closeImagePreview">
      <div class="fixed inset-0 bg-black/80"></div>
      <div class="relative max-w-4xl max-h-[90vh] p-2">
        <button
          @click.stop="closeImagePreview"
          class="absolute -top-2 -right-2 z-10 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
        <img
          :src="previewImageUrl"
          alt="图片预览"
          class="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain"
          @click.stop
        />
      </div>
    </div>
  </ListPageShell>
</template>
